import React from "react";
import { Briefcase, ShieldAlert, FileX, FileText, HandCoins, Building2, Handshake, HeartPulse, Gavel } from "lucide-react";
import { useJsonLd, SITE } from "../lib/seo";

const AREAS = [
    {
        icon: FileX,
        title: "Licenziamenti",
        slug: "licenziamenti",
        desc: "Impugnazione di licenziamenti illegittimi, disciplinari, per giustificato motivo oggettivo o collettivi. Assistenza nei termini di legge (60 + 180 giorni).",
    },
    {
        icon: ShieldAlert,
        title: "Mobbing e Demansionamento",
        slug: "mobbing-demansionamento",
        desc: "Tutela contro mobbing, straining, vessazioni e demansionamento. Risarcimento del danno biologico, professionale ed esistenziale.",
    },
    {
        icon: Briefcase,
        title: "Contratti di Lavoro",
        slug: "contratti-lavoro",
        desc: "Redazione, revisione e contenzioso su contratti subordinati, a termine, di apprendistato, di dirigenza e parasubordinati.",
    },
    {
        icon: Gavel,
        title: "Procedimenti Disciplinari",
        slug: "procedimenti-disciplinari",
        desc: "Gestione dell'intero iter, dalla contestazione all'irrogazione della sanzione, fino alla sua eventuale impugnazione.",
    },
    {
        icon: FileText,
        title: "Dimissioni e Risoluzioni",
        slug: "dimissioni-risoluzioni",
        desc: "Assistenza nelle dimissioni per giusta causa, risoluzioni consensuali, esodo incentivato e accordi transattivi.",
    },
    {
        icon: HandCoins,
        title: "NASpI e Welfare",
        slug: "naspi-welfare",
        desc: "Consulenza su indennità di disoccupazione NASpI, contenzioso INPS, contributi e prestazioni previdenziali.",
    },
    {
        icon: Building2,
        title: "Diritto Sindacale e d'Impresa",
        slug: "diritto-sindacale-impresa",
        desc: "Relazioni industriali, contrattazione collettiva, trasferimenti d'azienda, ristrutturazioni e procedure di licenziamento collettivo.",
    },
    {
        icon: Handshake,
        title: "Conciliazione Stragiudiziale",
        slug: "conciliazione-stragiudiziale",
        desc: "Conciliazione e mediazione tra Datore e Lavoratore: accordi transattivi, verbali in sede protetta e procedure deflattive del contenzioso.",
    },
    {
        icon: HeartPulse,
        title: "Infortuni sul Lavoro",
        slug: "infortuni-lavoro",
        desc: "Assistenza in caso di infortunio sul lavoro e malattia professionale: tutele INAIL, danno differenziale e responsabilità del datore.",
    },
];

export default function PracticeAreas() {
    // JSON-LD Service schema per ogni area di pratica (SEO: ranking su "[servizio] avvocato Piacenza")
    useJsonLd(
        AREAS.map((area) => ({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${SITE.url}/#service-${area.slug}`,
            "name": area.title,
            "description": area.desc,
            "serviceType": area.title,
            "category": "Diritto del Lavoro",
            "provider": { "@id": `${SITE.url}/#legalservice` },
            "areaServed": [
                { "@type": "City", "name": "Piacenza" },
                { "@type": "City", "name": "Milano" },
                { "@type": "City", "name": "Lecce" },
                { "@type": "Country", "name": "Italia" },
            ],
            "audience": {
                "@type": "Audience",
                "audienceType": "Lavoratori e Aziende",
            },
            "url": `${SITE.url}/#aree`,
        })),
        "practice-areas-services-jsonld",
    );

    return (
        <section
            id="aree"
            data-testid="practice-areas"
            className="py-24 lg:py-36 bg-navy relative overflow-hidden"
        >
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.85'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
            />

            <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
                <div className="max-w-3xl mb-20">
                    <div className="flex items-center gap-4 mb-6">
                        <span className="gold-rule" />
                        <span className="label-eyebrow">Aree di Competenza</span>
                    </div>
                    <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Una <span className="italic text-gold">specializzazione</span> a tutto tondo nel diritto del lavoro.
                    </h2>
                    <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed">
                        Lo Studio offre assistenza in ogni fase del rapporto di lavoro: dalla costituzione del rapporto fino all'eventuale contenzioso giudiziale, in modo multidisciplinare e con la collaborazione di colleghi esperti, con attenzione costante all'evoluzione normativa e giurisprudenziale.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                    {AREAS.map((area, idx) => {
                        const Icon = area.icon;
                        return (
                            <div
                                key={idx}
                                data-testid={`area-card-${idx}`}
                                className="bg-navy p-8 lg:p-10 group transition-all duration-500 hover:bg-navy-soft cursor-default relative"
                            >
                                <div className="absolute top-0 left-0 right-0 h-px bg-transparent group-hover:bg-gold transition-colors duration-500" />
                                <Icon className="text-gold mb-6" size={32} strokeWidth={1.5} />
                                <h3 className="font-serif text-white text-2xl mb-4 group-hover:text-gold transition-colors duration-300">
                                    {area.title}
                                </h3>
                                <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                                    {area.desc}
                                </p>
                                <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-gold/70 font-mono">
                                    0{idx + 1} / 0{AREAS.length}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
